import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export type PrestacaoStatus =
  | 'RASCUNHO'
  | 'PRONTA'
  | 'AUTOMATING'
  | 'AGUARDANDO_HUMANO'
  | 'CONCLUIDA'
  | 'ERRO'
  | 'CANCELADA';

export interface Prestacao {
  id: string;
  workspaceId: string;
  acordoId: string;
  competencia: string;
  status: PrestacaoStatus;
  snapshotJson: Record<string, unknown> | null;
  pdfR2Key: string | null;
  replayR2Key: string | null;
  iniciadaEm: string | null;
  aguardandoHumanoDesde: string | null;
  concluidaEm: string | null;
  concluidaPor: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PreflightIssue {
  severity: 'block' | 'warn';
  code: string;
  message: string;
}

export interface PreflightResult {
  issues: PreflightIssue[];
  snapshot: {
    pagamentos: { count: number; total: string; pendentes: number };
    receitas: { count: number; total: string };
    empregados: { ativos: number };
    bens: { count: number };
    contratos: { count: number };
    documentosFiscais: { count: number };
  };
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/prestacoes`;
}

export function usePrestacoes(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['prestacoes', workspaceId],
    queryFn: () => api.get<{ items: Prestacao[] }>(`${basePath(workspaceId!)}/`),
  });
}

export function usePreflight(workspaceId: string | null | undefined) {
  return useMutation({
    mutationFn: (competencia: string) =>
      api.post<PreflightResult>(`${basePath(workspaceId!)}/preflight`, { competencia }),
  });
}

export function useCreatePrestacao(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { acordoId: string; competencia: string }) =>
      api.post<{ prestacao: Prestacao; preflight: PreflightResult }>(
        `${basePath(workspaceId!)}/`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prestacoes', workspaceId] });
    },
  });
}

// ─── Preview (Onda 6) ────────────────────────────────────────────────────────

export interface CategoriaSnapshot {
  slug: string;
  label: string;
  total: number;
  percentual: number;
  denominador: number;
  denominadorCodigo: string;
  status: 'OK' | 'WARN' | 'BLOCK';
}

export interface PreviewSnapshot {
  pagamentos: { count: number; total: string; pendentes: number };
  receitas: { count: number; total: string };
  empregados: { ativos: number };
  bens: { count: number };
  contratos: { count: number };
  documentosFiscais: { count: number };
  conciliacao: { status: 'OK' | 'WARN' | 'BLOCK'; descricao?: string };
  categorias: CategoriaSnapshot[];
}

export interface PreviewResponse {
  snapshot: PreviewSnapshot;
  issues: PreflightIssue[];
  previewToken: string;
  tokenExpiresAt: string; // ISO
}

export interface DrillDownItem {
  id: string;
  kind: string;
  [key: string]: unknown;
}

export interface DrillDownPage {
  items: DrillDownItem[];
  nextCursor: string | null;
  total: number;
}

/** Hook utilitário de debounce (sem dep extra) */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(value), delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value, delay]);

  return debounced;
}

export function usePreview(
  workspaceId: string | null | undefined,
  acordoId: string | null | undefined,
  competencia: string | null | undefined,
  opts?: { enabled?: boolean },
) {
  const explicitEnabled = opts?.enabled ?? true;
  return useQuery({
    queryKey: ['prestacao-preview', workspaceId, acordoId, competencia],
    queryFn: () =>
      api.post<PreviewResponse>(`/v1/workspaces/${workspaceId!}/prestacoes/preview`, {
        acordoId,
        competencia,
      }),
    staleTime: 30_000,
    enabled: explicitEnabled && !!workspaceId && !!acordoId && !!competencia,
  });
}

export function usePreviewItems(
  workspaceId: string | null | undefined,
  params: {
    kind: string;
    acordoId: string;
    competencia: string;
    limit?: number;
  },
) {
  return useInfiniteQuery({
    queryKey: ['prestacao-preview-items', workspaceId, params.kind, params.acordoId, params.competencia],
    queryFn: ({ pageParam }) =>
      api.get<DrillDownPage>(
        `/v1/workspaces/${workspaceId!}/prestacoes/preview/items?kind=${params.kind}&acordoId=${params.acordoId}&competencia=${params.competencia}&limit=${params.limit ?? 50}${pageParam ? `&cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!workspaceId && !!params.kind && !!params.acordoId && !!params.competencia,
  });
}

export function useCreatePrestacaoFromPreview(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { acordoId: string; competencia: string; previewToken: string }) =>
      api.post<{ prestacao: Prestacao }>(
        `/v1/workspaces/${workspaceId!}/prestacoes`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prestacoes', workspaceId] });
    },
    onError: (err) => {
      const apiErr = err as { status?: number; code?: string };
      if (apiErr.status === 409 && apiErr.code === 'SNAPSHOT_STALE') {
        toast.error(
          'Os dados foram atualizados desde a sua prévia. Recarregue para ver os números atuais.',
        );
      } else if (apiErr.status === 403) {
        toast.error('Você não tem permissão para acessar este convênio.');
      } else {
        toast.error(err instanceof Error ? err.message : 'Falha ao iniciar prestação.');
      }
    },
  });
}

export function useDeletePrestacao(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prestacoes', workspaceId] });
    },
  });
}

export function useGeneratePrestacaoPdf(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ url: string; r2Key: string }>(`${basePath(workspaceId!)}/${id}/pdf`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prestacoes', workspaceId] });
    },
  });
}

// ─── Automação (Fase 3) ──────────────────────────────────────────────────────

export type JobStatus = 'QUEUED' | 'RUNNING' | 'WAITING_HUMAN' | 'DONE' | 'FAILED';
export type FaseStatus = 'STARTED' | 'DONE' | 'FAILED' | 'HEALED';

export interface FaseEntry {
  id: string;
  fase: number;
  status: FaseStatus;
  startedAt: string;
  finishedAt: string | null;
  errorMessage: string | null;
  screenshotR2Key: string | null;
}

export interface JobSnapshot {
  id: string;
  prestacaoId: string;
  workspaceId: string;
  provider: string;
  sessionId: string | null;
  liveViewUrl: string | null;
  status: JobStatus;
  currentFase: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  fases: FaseEntry[];
}

export interface PlaybookFaseDef {
  fase: number;
  nome: string;
}

export function useFasesCatalog(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['prestacao', 'fases'],
    queryFn: () => api.get<{ items: PlaybookFaseDef[] }>(`${basePath(workspaceId!)}/fases`),
    staleTime: 60 * 60 * 1000,
  });
}

export function useJob(workspaceId: string | null | undefined, prestacaoId: string | null) {
  return useQuery({
    enabled: !!workspaceId && !!prestacaoId,
    queryKey: ['prestacao', 'job', prestacaoId],
    queryFn: () =>
      api.get<JobSnapshot | { id: null }>(`${basePath(workspaceId!)}/${prestacaoId}/job`),
    refetchInterval: (q) => {
      const d = q.state.data as JobSnapshot | { id: null } | undefined;
      if (!d || !('status' in d)) return 4000;
      if (d.status === 'RUNNING' || d.status === 'QUEUED') return 2000;
      return false;
    },
  });
}

export function useStartAutomation(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prestacaoId: string) =>
      api.post<{ jobId: string; liveViewUrl: string | null }>(
        `${basePath(workspaceId!)}/${prestacaoId}/start`,
      ),
    onSuccess: (_, prestacaoId) => {
      qc.invalidateQueries({ queryKey: ['prestacao', 'job', prestacaoId] });
      qc.invalidateQueries({ queryKey: ['prestacoes', workspaceId] });
    },
  });
}

export function useCancelAutomation(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prestacaoId: string) =>
      api.post<{ ok: boolean }>(`${basePath(workspaceId!)}/${prestacaoId}/cancel`),
    onSuccess: (_, prestacaoId) => {
      qc.invalidateQueries({ queryKey: ['prestacao', 'job', prestacaoId] });
      qc.invalidateQueries({ queryKey: ['prestacoes', workspaceId] });
    },
  });
}

export function useConcluirPrestacao(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prestacaoId: string) =>
      api.post<{ ok: boolean }>(`${basePath(workspaceId!)}/${prestacaoId}/concluir`),
    onSuccess: (_, prestacaoId) => {
      qc.invalidateQueries({ queryKey: ['prestacao', 'job', prestacaoId] });
      qc.invalidateQueries({ queryKey: ['prestacoes', workspaceId] });
    },
  });
}
